import { useMemo } from "react";
import { calculateFinalTotal } from "../Utils/AccountingUtil";
import { getInvoiceBalance } from "../Utils/PaymentUtils";

/**
 * The figures a read-only screen shows for an invoice that has already been
 * posted.
 *
 * `transaction.total` is authoritative: it is what the ledger, the ageing
 * report and the payment endpoint all work from, and it is net of any tax the
 * counterparty withheld. Recomputing from the lines is only a tripwire — when
 * the two disagree we render the posted number and say so in the console,
 * because a silent disagreement is how the last rounding bug survived 54
 * invoices.
 *
 * The edit forms deliberately do not use this: while you are changing lines
 * the posted total is stale by design, so they call calculateFinalTotal.
 */
export const useInvoiceTotals = (transaction, taxDirectory = null) =>
  useMemo(() => {
    const items = transaction?.items?.items || [];
    const discountType =
      transaction?.discount?.type === "PERCENTAGE" ? "percentage" : "fixed";

    const calculations = calculateFinalTotal(
      items,
      discountType,
      transaction?.discount?.value,
      transaction?.discount?.applyAfterTax || false,
      taxDirectory,
      transaction?.taxRounding,
    );
    // Pembulatan is posted with the invoice, so the screen shows the posted
    // figure, like the total.
    if (typeof transaction?.roundingAmount === "number") {
      calculations.roundingAmount = transaction.roundingAmount;
    }

    const computedTotal = calculations.grandTotal;
    const posted =
      typeof transaction?.total === "number" ? transaction.total : null;
    // Half a sen of slack: both sides round to two decimals, so anything
    // larger is a real disagreement and not float dust.
    const mismatch = posted != null && Math.abs(posted - computedTotal) > 0.005;

    if (mismatch) {
      // eslint-disable-next-line no-console
      console.warn(
        "[accounting] the invoice total on screen disagrees with the posted one",
        {
          id: transaction?.id,
          invoiceNumber: transaction?.invoiceNumber,
          posted,
          computed: computedTotal,
        },
      );
    }

    const total = posted ?? computedTotal;

    return {
      calculations,
      total,
      computedTotal,
      mismatch,
      // totalPayment, balanceDue, settlementAmount and isSettled.
      ...getInvoiceBalance(total, transaction?.payments?.items),
    };
  }, [transaction, taxDirectory]);

export default useInvoiceTotals;
